import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import companyContextService from '../../services/companyContextService';
import LoadingSpinner from '../common/LoadingSpinner';
import ContextDocumentsManager from './ContextDocumentsManager';

const CompanyContext = () => {
  const [context, setContext] = useState({
    company_name: '',
    company_description: '',
    website_url: '',
    industry: '',
    headquarters_location: '',
    employee_count: '',
    founded_year: '',
    key_services: [],
    key_differentiators: '',
    certifications: [],
    contact_info: {
      phone: '',
      email: '',
      address: ''
    },
    additional_context: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    loadCompanyContext();
  }, []);

  const loadCompanyContext = async () => {
    setLoading(true);
    try {
      const data = await companyContextService.getCompanyContext();
      if (data) {
        setContext({
          ...context,
          ...data,
          // Ensure arrays are arrays
          key_services: Array.isArray(data.key_services) ? data.key_services : [],
          certifications: Array.isArray(data.certifications) ? data.certifications : [],
          // Ensure contact_info is an object
          contact_info: typeof data.contact_info === 'object' && data.contact_info 
            ? data.contact_info 
            : { phone: '', email: '', address: '' }
        });
      }
    } catch (error) {
      console.error('Failed to load company context:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (field, value) => {
    setContext(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactInfoChange = (field, value) => {
    setContext(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        [field]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value.trim()) {
      setContext(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setContext(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      await companyContextService.updateCompanyContext(context);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save company context:', error);
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
        <span className="ml-3 text-gray-600">Loading company context...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon="building" className="mr-3 text-blue-500" />
              Company Context
            </h2>
            <p className="text-gray-600 mt-2">
              Configure your company information to personalize AI-generated proposal content.
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 flex items-center"
            >
              {saving ? (
                <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
              ) : (
                <FontAwesomeIcon icon="save" className="mr-2" />
              )}
              Save
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {saveSuccess && (
          <div className="mt-4 p-3 bg-green-100 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon="check-circle" className="text-green-600 mr-2" />
              <span className="text-green-800">Company context saved successfully!</span>
            </div>
          </div>
        )}
      </div>


      {/* Basic Company Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FontAwesomeIcon icon="info-circle" className="mr-2 text-blue-500" />
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              value={context.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your Company Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website URL
            </label>
            <input
              type="url"
              value={context.website_url}
              onChange={(e) => handleInputChange('website_url', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.yourcompany.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industry
            </label>
            <input
              type="text"
              value={context.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Technology Services, Manufacturing"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Headquarters Location
            </label>
            <input
              type="text"
              value={context.headquarters_location}
              onChange={(e) => handleInputChange('headquarters_location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="City, State, Country"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Size
            </label>
            <select
              value={context.employee_count}
              onChange={(e) => handleInputChange('employee_count', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select company size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-500">201-500 employees</option>
              <option value="501-1000">501-1000 employees</option>
              <option value="1000+">1000+ employees</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Founded Year
            </label>
            <input
              type="number"
              value={context.founded_year}
              onChange={(e) => handleInputChange('founded_year', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="2020"
              min="1800"
              max={new Date().getFullYear()}
            />
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Description
          </label>
          <textarea
            value={context.company_description}
            onChange={(e) => handleInputChange('company_description', e.target.value)}
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief description of your company, what you do, and your mission..."
          />
        </div>
      </div>

      {/* Services and Capabilities */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FontAwesomeIcon icon="cogs" className="mr-2 text-green-500" />
          Services & Capabilities
        </h3>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Key Services
            </label>
            <ArrayInput
              items={context.key_services}
              onAdd={(value) => handleArrayAdd('key_services', value)}
              onRemove={(index) => handleArrayRemove('key_services', index)}
              placeholder="Add a service (e.g., Software Development, Consulting)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Key Differentiators
            </label>
            <textarea
              value={context.key_differentiators}
              onChange={(e) => handleInputChange('key_differentiators', e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="What sets your company apart from competitors? Unique strengths, experience, approach..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certifications & Awards
            </label>
            <ArrayInput
              items={context.certifications}
              onAdd={(value) => handleArrayAdd('certifications', value)}
              onRemove={(index) => handleArrayRemove('certifications', index)}
              placeholder="Add certification or award (e.g., ISO 9001, Best Employer 2023)"
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FontAwesomeIcon icon="phone" className="mr-2 text-purple-500" />
          Contact Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={context.contact_info.phone || ''}
              onChange={(e) => handleContactInfoChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+1-555-123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={context.contact_info.email || ''}
              onChange={(e) => handleContactInfoChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="contact@yourcompany.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mailing Address
            </label>
            <input
              type="text"
              value={context.contact_info.address || ''}
              onChange={(e) => handleContactInfoChange('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="123 Business St, City, State, ZIP"
            />
          </div>
        </div>
      </div>

      {/* Additional Context */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FontAwesomeIcon icon="edit" className="mr-2 text-orange-500" />
          Additional Context
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Information for AI
          </label>
          <textarea
            value={context.additional_context}
            onChange={(e) => handleInputChange('additional_context', e.target.value)}
            rows="6"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Add any specific information you want the AI to include in proposals: unique methodologies, special partnerships, notable achievements, specific expertise areas, etc."
          />
          <p className="mt-2 text-sm text-gray-500">
            This information will be included in AI prompts to help generate more accurate and personalized proposal content.
          </p>
        </div>
      </div>

      {/* Context Documents Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <ContextDocumentsManager />
      </div>

    </div>
  );
};

// Helper component for array inputs
const ArrayInput = ({ items, onAdd, onRemove, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={placeholder}
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <FontAwesomeIcon icon="plus" />
        </button>
      </div>
      
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
            >
              {item}
              <button
                onClick={() => onRemove(index)}
                className="ml-2 hover:text-blue-600"
              >
                <FontAwesomeIcon icon="times" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyContext;